// Review gate — the estimate disclaimer and the "get this reviewed before you share it" rule, plus
// the pure logic behind the once-per-deal interstitial in front of "Generate value slides".
//
// Leaf module by design (no React, no asset imports) for the reason documented at the top of
// account-link.ts: `__tests__` compiles under `tsconfig.tests.json`, which has no asset-module type
// declarations, so anything reachable from a test must not pull in a component's import graph.
// The localStorage try/catch wrappers stay in CalculatorApp, matching readStoredBmRows() and
// ValueStoryDeck's setAudience(); only pure string -> string logic lives here.

/** The acknowledged-deal list. Added to the key inventory in this app's CLAUDE.md. */
export const REVIEW_ACK_KEY = 'clay-value-review-ack-v1';

/**
 * Most-recent deals kept, so the list cannot grow without bound (~20 chars each, so ~1KB).
 * Eviction re-prompts on a long-dormant deal, which is the safe direction to fail.
 */
export const REVIEW_ACK_MAX = 50;

export const REVIEW_SLACK_URL = 'https://clay-hq.slack.com/archives/C0BMM3JAD9R';
export const REVIEW_SLACK_CHANNEL = '#proj-value-calculator';

/* ── copy ──
   Shared by the ROI-summary callout and the interstitial, so the channel link exists once. The deck
   and the customer workbook deliberately keep their OWN literals: the deck imports only
   engine/format, engine/migrate, data/icons and types (a boundary CLAUDE.md pins, since the
   calculator -> deck seam is the denormalized snapshot), and a 13px web callout, a 22px slide line
   and a 10pt Excel note cannot share one sentence without collapsing to a phrasing that reads badly
   in all three. Three literals is the decision, not an oversight — don't "DRY" them. */

export const ESTIMATE_HEADING = 'Value estimate — not a guarantee';
export const ESTIMATE_BODY =
  'These figures are modeled from the inputs on this page and will vary for your business.';
/** Rendered with REVIEW_SLACK_CHANNEL as a link, then a full stop. */
export const REVIEW_REQUIRED =
  'Before sharing with a buyer, this needs a review from the Value Advisory team. Request one in';

export const GATE_TITLE = 'One step before you share';
export const GATE_BODY =
  'This value story is a modeled estimate, not a guarantee of results. It needs a review from the Value Advisory team before it goes to a buyer.';
/** Rendered with REVIEW_SLACK_CHANNEL as a link, then a full stop. */
export const GATE_REQUEST = 'Request a review in';
export const GATE_ACK = 'Got it';

/** The `linkedAccount` fields this module reads — structural, so LinkedAccount satisfies it. */
export interface ReviewDeal {
  id: string;
  opportunityId?: string;
}

/**
 * The ack key for one sizing: the opportunity when there is one, else the account. A *deal* is an
 * opportunity, so a second opportunity at the same account re-prompts — keying on the account alone
 * would skip the warning there, which is the one false negative that matters. An account linked
 * without an opportunity and later relinked with one prompts twice; that fails toward showing the
 * warning, so it is the direction to accept.
 *
 * Null means "no deal identity" — demo mode and local-only mode, where the gate always shows and
 * nothing is stored. A single shared sentinel would let one dismissal in a throwaway demo silence
 * the warning for every later unlinked sizing.
 */
export function reviewDealId(linked: ReviewDeal | null): string | null {
  if (!linked) return null;
  return linked.opportunityId || linked.id;
}

/** Any unparseable or non-array payload reads as empty, so the gate shows again. */
function parseAcks(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Has this deal's warning already been acknowledged in this browser? Membership is the only question
 * asked, which is why the stored shape is a plain array of ids and not a map of timestamps nobody
 * reads — timestamps would invite an expiry policy that "once ever" rules out.
 */
export function hasAckedReview(raw: string | null, dealId: string | null): boolean {
  if (!dealId) return false;
  return parseAcks(raw).includes(dealId);
}

/** The JSON to store after an ack: this deal first, deduped, oldest evicted past REVIEW_ACK_MAX. */
export function withReviewAck(raw: string | null, dealId: string): string {
  const next = [dealId, ...parseAcks(raw).filter((x) => x !== dealId)].slice(0, REVIEW_ACK_MAX);
  return JSON.stringify(next);
}
